import React from 'react';
import { styled, Theme } from '@mui/material/styles';
import TrimbleMaps, { LngLatBoundsLike } from '@trimblemaps/trimblemaps-js';
import { AllGeoJSON, bbox } from '@turf/turf';

import { MapArea } from '../../interfaces/services/geography';
import { MapLayers, MapSources } from '../../utils/maps/enums';
import {
    fitMapToBounds,
    handleFillPopupMouseEnter,
    handlePopupMouseLeave,
    trimbleMapsPopup,
} from '../../utils/maps/mapUtils';
import { addAreasPolygonFillLayer, addAreasPolygonLineLayer } from '../../utils/maps/layers/areas';
import { AreasMapSourceProperties, addAreasSource } from '../../utils/maps/sources/areas';
import MapSpinner from '../Loaders/MapSpinner';

import '../../assets/CSS/trimbleMaps.css';

const classesPrefix = 'areaMap';

const classes = {
    map: `${classesPrefix}-map`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.map}`]: {
            height: '100%',
            minHeight: '300px',
        },
    };
});

interface AreaMapProps {
    /** Indicator to show loading spinner while data is fetching. */
    isFetchingData: boolean;
    /** Area name */
    areaName: string;
    /** Areas to add to the map */
    areas: MapArea[];
    /** MUI theme for styling the map layers */
    theme: Theme;
    /** Indicator to trigger a map resize function. */
    resize?: boolean;
}

interface AreaMapState {
    mapStyleLoaded: boolean;
}

let map: TrimbleMaps.Map | null = null;
const mapContainer = React.createRef<HTMLDivElement>();

class AreaMap extends React.Component<AreaMapProps, AreaMapState> {

    private hoveredPolygonId: string | number | null | undefined;

    constructor(props: AreaMapProps) {
        super(props);

        TrimbleMaps.APIKey = '173580F5C6F48F44B5006F78909958C1';

        this.state = {
            mapStyleLoaded: false,
        };

        this.hoveredPolygonId = null;
    }

    componentDidMount(): void {
        const latitude = 39.828347;
        const longitude = -98.579488;

        if (!mapContainer.current) { return; }

        map = new TrimbleMaps.Map({
            container: mapContainer.current, // Ties to an HTML DOM element
            center: [longitude, latitude], // Sets initial center of map
            style: TrimbleMaps.Common.Style.DATADARK, // Sets initial map style
            minZoom: 1.55, // To stop the zoom from showing multiple worlds
            zoom: 3, // Sets initial zoom level
        }).addControl(
            // Adds the zoom in/out and compass buttons
            new TrimbleMaps.NavigationControl()
        ).addControl(
            new TrimbleMaps.FullscreenControl()
        );

        map.setRegion(TrimbleMaps.Common.Region.WW);

        // README: The event `style.load` is no longer a public event of mapbox. We will need to use some combination of using `styledata` and `map.isStyleLoaded()` at some point.
        // However, these do not work the same and there are several open issues with mapbox that have yet to be resolved, such as this one:
        // https://github.com/mapbox/mapbox-gl-js/issues/9779
        // Once a map style is loaded, add any layers that were on back to the map
        map.on('style.load', (): void => {
            // The style.load event can fire after the component has been unmounted.
            if (map === null) {
                return;
            }

            this.removeAreasEventHandlers();
            this.addAreasToMap();

            // HACK: trigger a resize function to make map take up full width
            map.resize();
            this.setState({ mapStyleLoaded: true });
        });
    }

    componentDidUpdate(prevProps: AreaMapProps): void {
        // if the map is null, hold any CDU updates
        if (map === null) {
            return;
        }

        if (this.state.mapStyleLoaded === false) {
            return;
        }

        // add areas to map if props have updated
        if (prevProps.areas !== this.props.areas) {
            this.removeAreasEventHandlers();
            this.addAreasToMap();
        }

        // if resize prop is passed and is true force map to resize.
        if (this.props.resize === true) {
            map.resize();
        }
    }

    componentWillUnmount(): void {
        this.removeAreasEventHandlers();
    }

    handleFillLayerMouseenter = (e: TrimbleMaps.MapMouseEvent & {
        features?: TrimbleMaps.GeoJSONFeature[];
    } & TrimbleMaps.EventData): void => {
        if (map) {
            handleFillPopupMouseEnter(map, e, trimbleMapsPopup);

            // Update the feature state for the feature under the mouse.
            if (e.features && e.features.length > 0) {
                if (this.hoveredPolygonId !== null) {
                    map.setFeatureState(
                        { source: MapSources.areasSource, id: this.hoveredPolygonId },
                        { hover: false }
                    );
                }
                this.hoveredPolygonId = e.features[0].id;
                map.setFeatureState(
                    { source: MapSources.areasSource, id: this.hoveredPolygonId },
                    { hover: true }
                );
            }
        }
    };

    handleFillLayerMouseleave = (): void => {
        if (map) {
            handlePopupMouseLeave(map, trimbleMapsPopup);

            // Update the feature state of the previously hovered feature.
            if (this.hoveredPolygonId !== null) {
                map.setFeatureState(
                    { source: MapSources.areasSource, id: this.hoveredPolygonId },
                    { hover: false }
                );
            }
            this.hoveredPolygonId = null;
        }
    };

    addAreasToMap = (): void => {
        if (map) {
            // add map source
            const areasSource = addAreasSource({
                map,
                areas: this.props.areas || [],
                areaName: this.props.areaName,
                theme: this.props.theme,
            });

            // add area polygon fill
            addAreasPolygonFillLayer({
                map,
                mapSource: MapSources.areasSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.areaPolygonFillLayer, this.handleFillLayerMouseenter);
            map.on('mouseleave', MapLayers.areaPolygonFillLayer, this.handleFillLayerMouseleave);

            // add area polygon outline
            addAreasPolygonLineLayer({
                map,
                mapSource: MapSources.areasSource,
                isVisible: true,
            });

            const { data } = areasSource;
            if ((data as GeoJSON.FeatureCollection<GeoJSON.Geometry, AreasMapSourceProperties>).features.length > 0) {
                const boundingBox = bbox(data as AllGeoJSON);
                fitMapToBounds({
                    map,
                    bounds: boundingBox as LngLatBoundsLike,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    removeAreasEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.areaPolygonFillLayer) !== undefined) {
                map.off('mouseenter', MapLayers.areaPolygonFillLayer, this.handleFillLayerMouseenter);
                map.off('mouseleave', MapLayers.areaPolygonFillLayer, this.handleFillLayerMouseleave);
            }
        }
    };

    render(): React.JSX.Element {
        return (
            <StyledDiv
                id='trimble-map-container'
                ref={mapContainer}
                className={classes.map}
                data-qa='areaMap-container'
            >
                {
                    this.props.isFetchingData &&
                    <MapSpinner />
                }
            </StyledDiv>
        );
    }

}

export default AreaMap;
